import { FormEvent, useEffect, useMemo, useState } from "react";
import { Layout } from "../components/Layout";
import { Panel } from "../components/Panel";
import { DealWinPlanCard, GuidedPlanCard } from "../components/PricingPlanCards";
import { fetchPublicPricingPlans, publicPlanBadge, startPublicCheckout } from "../lib/api/publicBillingClient";
import type { RuntimeBillingPlan } from "../lib/api/runtimeWorkspaceClient";
import { getDealWinEnginePlans } from "../lib/billing/plans";
import { t, useWorkspaceLanguage, withLanguagePrefix } from "../lib/i18n";

function formatEuro(value: number, language: "de" | "en"): string {
  return new Intl.NumberFormat(language === "en" ? "en-US" : "de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0
  }).format(value);
}

function billingIntervalSuffix(plan: RuntimeBillingPlan, language: "de" | "en", perMonth: string): string {
  if (plan.billingInterval === "monthly") return perMonth;
  if (plan.billingInterval === "every_3_months") return language === "en" ? "net / 3 months" : "netto / 3 Monate";
  if (plan.billingInterval === "every_6_months") return language === "en" ? "net / 6 months" : "netto / 6 Monate";
  return language === "en" ? "net / year" : "netto / Jahr";
}

function extraDepartmentIntervalLabel(plan: RuntimeBillingPlan, language: "de" | "en", monthlyLabel: string): string {
  if (plan.billingInterval === "monthly") return monthlyLabel;
  if (plan.billingInterval === "every_3_months") return language === "en" ? "net per additional department / 3 months" : "netto je zusätzliche Abteilung / 3 Monate";
  if (plan.billingInterval === "every_6_months") return language === "en" ? "net per additional department / 6 months" : "netto je zusätzliche Abteilung / 6 Monate";
  return language === "en" ? "net per additional department / year" : "netto je zusätzliche Abteilung / Jahr";
}

function dealWinMonthlyAmount(value: number | undefined): number {
  return Math.round((value ?? 0) / 12);
}

function dealWinMonthlySuffix(language: "de" | "en"): string {
  return language === "en" ? "net / month" : "netto / Monat";
}

function dealWinAnnualEquivalent(value: number, language: "de" | "en"): string {
  return language === "en"
    ? `equals ${formatEuro(value, language)} net per year`
    : `entspricht ${formatEuro(value, language)} netto pro Jahr`;
}

export function Pricing() {
  const language = useWorkspaceLanguage();
  const labels = t(language).pricing;
  const [plans, setPlans] = useState<RuntimeBillingPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState("guided_digital_access_plus_6m");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [extraDepartmentCount, setExtraDepartmentCount] = useState(0);
  const [selectedDealWinPlanId, setSelectedDealWinPlanId] = useState("");
  const [extraDealWinUserCount, setExtraDealWinUserCount] = useState(0);
  const [message, setMessage] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const purchaseStatus = useMemo(() => (
    typeof window === "undefined" ? undefined : new URLSearchParams(window.location.search).get("purchase")
  ), []);
  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);
  const dealWinPlans = useMemo(() => getDealWinEnginePlans(), []);
  const selectedDealWinPlan = dealWinPlans.find((plan) => plan.id === selectedDealWinPlanId);
  const bundleDiscountPreviewActive = Boolean(selectedDealWinPlan && selectedDealWinPlan.id !== "deal_win_engine_single_12m");
  const safeExtraDepartmentCount = Math.max(0, Math.trunc(Number.isFinite(extraDepartmentCount) ? extraDepartmentCount : 0));
  const addonNetEur = selectedPlan ? safeExtraDepartmentCount * selectedPlan.extraDepartmentPriceNetEur : 0;
  const totalNetEur = selectedPlan ? selectedPlan.priceNetEur + addonNetEur : 0;
  const safeExtraDealWinUserCount = selectedDealWinPlan?.id === "deal_win_engine_single_12m" ? 0 : Math.max(0, Math.trunc(Number.isFinite(extraDealWinUserCount) ? extraDealWinUserCount : 0));
  const selectedDealWinTotalUsers = selectedDealWinPlan ? (selectedDealWinPlan.includedUsers ?? 1) + safeExtraDealWinUserCount : 0;
  const dealWinMonthlyAddonUnitNetEur = selectedDealWinPlan ? dealWinMonthlyAmount(selectedDealWinPlan.extraUserPriceNetEur) : 0;
  const dealWinAddonMonthlyNetEur = safeExtraDealWinUserCount * dealWinMonthlyAddonUnitNetEur;
  const dealWinAddonNetEur = selectedDealWinPlan ? safeExtraDealWinUserCount * (selectedDealWinPlan.extraUserPriceNetEur ?? 0) : 0;
  const dealWinBaseMonthlyNetEur = selectedDealWinPlan ? dealWinMonthlyAmount(selectedDealWinPlan.priceNetEur) : 0;
  const dealWinTotalMonthlyNetEur = dealWinBaseMonthlyNetEur + dealWinAddonMonthlyNetEur;
  const dealWinTotalNetEur = selectedDealWinPlan ? selectedDealWinPlan.priceNetEur + dealWinAddonNetEur : 0;
  const addonLine = selectedPlan && safeExtraDepartmentCount > 0
    ? `${safeExtraDepartmentCount} × ${formatEuro(selectedPlan.extraDepartmentPriceNetEur, language)} = ${formatEuro(addonNetEur, language)} ${billingIntervalSuffix(selectedPlan, language, labels.perMonth)}`
    : labels.noExtraDepartments;
  const durationLine = selectedPlan?.durationMonths
    ? `${selectedPlan.durationMonths} ${labels.months}`
    : labels.monthlyNotice;

  useEffect(() => {
    document.title = language === "en"
      ? "Pricing | HAUFFE OS Customer Value Workspace"
      : "Preise | HAUFFE OS Customer Value Workspace";
    void fetchPublicPricingPlans()
      .then((data) => {
        setPlans(data.plans);
        if (data.plans.some((plan) => plan.id === data.bestValuePlanId)) setSelectedPlanId(data.bestValuePlanId);
      })
      .catch((caught: unknown) => setMessage(caught instanceof Error ? caught.message : labels.loadError));
  }, [labels.loadError, language]);

  async function submitCheckout(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedPlan) return;
    setIsSubmitting(true);
    setMessage(undefined);
    try {
      const result = await startPublicCheckout({
        planId: selectedPlan.id,
        email,
        companyName,
        buyerName,
        extraDepartmentCount
      });
      window.location.href = result.checkoutUrl;
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : labels.checkoutError);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function submitDealWinCheckout() {
    if (!selectedDealWinPlan) return;
    setIsSubmitting(true);
    setMessage(undefined);
    try {
      const result = await startPublicCheckout({
        planId: selectedDealWinPlan.id,
        email,
        companyName,
        buyerName,
        extraUserCount: safeExtraDealWinUserCount
      });
      window.location.href = result.checkoutUrl;
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : labels.checkoutError);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Layout
      eyebrow={labels.eyebrow}
      title={labels.title}
      description={labels.description}
    >
      {purchaseStatus === "success" ? (
        <Panel title={labels.successTitle} variant="accent">
          <p>{labels.successBody}</p>
          <p className="form-note">{labels.successNote}</p>
        </Panel>
      ) : null}
      {purchaseStatus === "cancelled" ? (
        <Panel title={labels.cancelledTitle} variant="soft">
          <p>{labels.cancelledBody}</p>
        </Panel>
      ) : null}
      {message ? <p className="form-error" role="alert">{message}</p> : null}

      <section className="pricing-product-section" aria-labelledby="guided-pricing">
        <h2 id="guided-pricing">{labels.guidedSectionTitle}</h2>
        <div className="grid grid-4 billing-plan-grid">
          {plans.map((plan) => (
            <GuidedPlanCard
              key={plan.id}
              className={plan.id === selectedPlanId ? "billing-plan-card-selected" : ""}
              badge={<span className="pricing-card__badge">{publicPlanBadge(plan, language)}</span>}
              title={plan.displayName}
              description={plan.description}
              price={formatEuro(plan.priceNetEur, language)}
              priceSuffix={billingIntervalSuffix(plan, language, labels.perMonth)}
              features={(
                <>
                  {plan.includedDepartments} {labels.includedDepartments} · {formatEuro(plan.extraDepartmentPriceNetEur, language)}{" "}
                  {extraDepartmentIntervalLabel(plan, language, labels.extraDepartmentMonthly)}
                </>
              )}
              note={(
                <>
                  {labels.executiveIncluded}<br />
                  {plan.durationMonths ? `${plan.durationMonths} ${labels.months}` : labels.monthlyNotice}
                </>
              )}
              addonConfig={plan.id === selectedPlanId ? (
                <div className="pricing-card-addon-control">
                  <label>
                    {labels.extraDepartmentsProduct}
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={extraDepartmentCount}
                      onChange={(event) => setExtraDepartmentCount(Math.max(0, Math.trunc(Number(event.target.value) || 0)))}
                    />
                  </label>
                  {safeExtraDepartmentCount > 0 ? (
                    <p>{safeExtraDepartmentCount} × {formatEuro(plan.extraDepartmentPriceNetEur, language)} = {formatEuro(addonNetEur, language)} {billingIntervalSuffix(plan, language, labels.perMonth)}</p>
                  ) : null}
                </div>
              ) : null}
              action={(
                <button
                  className={plan.id === selectedPlanId ? "button button-primary" : "button button-secondary"}
                  type="button"
                  onClick={() => {
                    if (plan.id !== selectedPlanId) setSelectedPlanId(plan.id);
                  }}
                >
                  {plan.id === selectedPlanId ? labels.selected : labels.selectPlan}
                </button>
              )}
            />
          ))}
        </div>
      </section>

      <section className="pricing-product-section" aria-labelledby="deal-win-pricing">
        <h2 id="deal-win-pricing">{labels.dealWinSectionTitle}</h2>
        <p className="form-note">{bundleDiscountPreviewActive ? labels.bundleDiscountActive : labels.bundleDiscountHint}</p>
        <div className="deal-win-pricing-grid">
          {dealWinPlans.map((plan) => (
            <DealWinPlanCard
              key={plan.id}
              plan={plan}
              language={language}
              selected={plan.id === selectedDealWinPlanId}
              singleBadge={<span className="pricing-card__badge">{labels.dealWinSingleBadge}</span>}
              bundleBadge={<span className="pricing-card__badge">{labels.dealWinBundleBadge}</span>}
              includedUsersLabel={labels.includedUsers}
              durationLabel={labels.months}
              intervalLabel={billingIntervalSuffix(plan, language, labels.perMonth)}
              note={labels.dealWinCheckoutPrepared}
              addonConfig={plan.id === selectedDealWinPlanId && plan.id !== "deal_win_engine_single_12m" ? (
                <div className="pricing-card-addon-control">
                  <label>
                    {labels.extraDealWinUsersProduct}
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={extraDealWinUserCount}
                      onChange={(event) => setExtraDealWinUserCount(Math.max(0, Math.trunc(Number(event.target.value) || 0)))}
                    />
                  </label>
                  {safeExtraDealWinUserCount > 0 ? (
                    <p>{safeExtraDealWinUserCount} × {formatEuro(dealWinMonthlyAmount(plan.extraUserPriceNetEur), language)} = {formatEuro(safeExtraDealWinUserCount * dealWinMonthlyAmount(plan.extraUserPriceNetEur), language)} {dealWinMonthlySuffix(language)}</p>
                  ) : null}
                </div>
              ) : null}
              action={(
                <button
                  className={plan.id === selectedDealWinPlanId ? "button button-primary" : "button button-secondary"}
                  type="button"
                  onClick={() => {
                    if (plan.id !== selectedDealWinPlanId) {
                      setSelectedDealWinPlanId(plan.id);
                      setExtraDealWinUserCount(0);
                    }
                  }}
                >
                  {plan.id === selectedDealWinPlanId ? labels.selected : labels.dealWinSelect}
                </button>
              )}
            />
          ))}
        </div>
      </section>

      <Panel title={labels.formTitle} variant="accent">
        <form className="purchase-layout" onSubmit={submitCheckout}>
          <div className="form form-grid purchase-form">
            <label>
              {labels.email}
              <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
            </label>
            <label>
              {labels.company}
              <input value={companyName} onChange={(event) => setCompanyName(event.target.value)} required />
            </label>
            <label>
              {labels.name}
              <input value={buyerName} onChange={(event) => setBuyerName(event.target.value)} />
            </label>
            <p className="form-note form-wide">{labels.legalNote}</p>
          </div>
          <aside className="purchase-summary" aria-label={labels.summaryTitle}>
            <h3>{labels.summaryTitle}</h3>
            <dl>
              <div>
                <dt>{labels.selectedPlan}</dt>
                <dd>{selectedPlan?.displayName ?? labels.summaryEmpty}</dd>
              </div>
              <div>
                <dt>{labels.dealWinSectionTitle}</dt>
                <dd>{selectedDealWinPlan?.displayName ?? "—"}</dd>
              </div>
              <div>
                <dt>{labels.duration}</dt>
                <dd>{durationLine}</dd>
              </div>
              <div>
                <dt>{labels.includedDepartments}</dt>
                <dd>{selectedPlan?.includedDepartments ?? "—"}</dd>
              </div>
              <div>
                <dt>{labels.extraDepartments}</dt>
                <dd>{safeExtraDepartmentCount}</dd>
              </div>
              <div>
                <dt>{labels.basePrice}</dt>
                <dd>{selectedPlan ? formatEuro(selectedPlan.priceNetEur, language) : "—"}</dd>
              </div>
              <div>
                <dt>{labels.addonPrice}</dt>
                <dd>{addonLine}</dd>
              </div>
              <div className="purchase-summary__total">
                <dt>{labels.totalPrice}</dt>
                <dd>{selectedPlan ? `${formatEuro(totalNetEur, language)} ${billingIntervalSuffix(selectedPlan, language, labels.perMonth)}` : "—"}</dd>
              </div>
              {selectedDealWinPlan ? (
                <>
                  <div>
                    <dt>{labels.includedUsers}</dt>
                    <dd>{selectedDealWinPlan.includedUsers ?? 1}</dd>
                  </div>
                  <div>
                    <dt>{labels.extraDealWinUsers}</dt>
                    <dd>{safeExtraDealWinUserCount}</dd>
                  </div>
                  <div>
                    <dt>{labels.totalDealWinUsers}</dt>
                    <dd>{selectedDealWinTotalUsers}</dd>
                  </div>
                  <div className="purchase-summary__total">
                    <dt>{labels.dealWinTotalPrice}</dt>
                    <dd>
                      <span>{formatEuro(dealWinTotalMonthlyNetEur, language)} {dealWinMonthlySuffix(language)}</span>
                      <small>{dealWinAnnualEquivalent(dealWinTotalNetEur, language)}</small>
                    </dd>
                  </div>
                </>
              ) : null}
            </dl>
            <p>{labels.vatNote}</p>
            {selectedDealWinPlan ? <p className="form-note">{bundleDiscountPreviewActive && selectedPlan ? labels.bundleCheckoutPrepared : labels.dealWinCheckoutPrepared}</p> : null}
            {selectedDealWinPlan && selectedPlan ? <p className="form-note">{labels.separateCheckoutNote}</p> : null}
            <p>{labels.stripeNote}</p>
            <div className="form-actions">
              {selectedPlan ? <button className="button button-primary" type="submit" disabled={isSubmitting}>
                {labels.buyNow}
              </button> : null}
              {selectedDealWinPlan ? (
                <button className="button button-gold" type="button" disabled={isSubmitting} onClick={() => void submitDealWinCheckout()}>
                  {labels.dealWinBuyNow}
                </button>
              ) : null}
              <a className="button button-secondary" href={withLanguagePrefix("/login", language)}>
                {labels.existingCustomer}
              </a>
            </div>
          </aside>
        </form>
      </Panel>
    </Layout>
  );
}
